import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { GridService } from '../shared/grid/grid.service';
import { GridModel } from '../shared/grid/grid.model';
import { SamplingPlan, SamplingPlanListResponse } from '../models/samplingPlan.model';
import { SamplingPlanStatus } from '../enums/samplingPlanStatus.enum';
import { SamplingPlanMatrix, SamplingPlanMatrixWrite } from '../models/samplingPlanMatrix.model';

// DASH-2690 (G11v2): parent Sampling Plan service. controllerName 'SamplingPlan' -> api/SamplingPlan/* (the BE
// keeps the api/[controller] route alongside the REST-literal api/sampling-plans). GetList/Create/Update/Delete are
// inherited from GridService. DASH-2706 adds the plan-detail SEG x agent matrix reads/writes against the REST-literal
// api/sampling-plans/{id}/matrix route.
@Injectable({
    providedIn: 'root',
})
export class SamplingPlanService extends GridService<SamplingPlan> {
    constructor(http: HttpClient) {
        super('SamplingPlan', http);
    }

    override get(
        filter: string,
        orderBy: string,
        sort: string,
        page: number,
        pageSize: number,
        filterBy: string,
        headers?: HttpHeaders,
    ): Observable<GridModel<SamplingPlanListResponse>> {
        return this.http.get<GridModel<SamplingPlanListResponse>>(
            this.getApiUrl(`${this.controllerName}/GetList`),
            {
                params: this.createParamsWithFilterBy(filter, orderBy, sort, page, pageSize, filterBy),
                headers,
            },
        );
    }

    getMatrix(id: string): Observable<SamplingPlanMatrix> {
        return this.http.get<SamplingPlanMatrix>(this.getApiUrl(`sampling-plans/${id}/matrix`));
    }

    saveMatrix(id: string, payload: SamplingPlanMatrixWrite): Observable<SamplingPlanMatrix> {
        return this.http.post<SamplingPlanMatrix>(this.getApiUrl(`sampling-plans/${id}/matrix`), payload);
    }

    // DASH-2742 (G11v2 approve): flip a plan's lifecycle status through the SAME generic Update the create/edit
    // dialog uses (SamplingPlanDto.Status is settable and the BE ApplyApprovalStamp stamps/clears ApprovedBy +
    // ApprovedDate on the transition into / out of Approved). It first reads the full editable plan so the Update
    // payload carries every field — the detail matrix only knows the status, and a partial Patch of Draft=0 could be
    // read as "not provided". Reused by both the plan list row action and the plan-detail header action.
    setStatus(id: string, status: SamplingPlanStatus): Observable<Object> {
        return this.getById(id).pipe(
            switchMap((plan) => this.update({ ...plan, status })),
        );
    }
}
